import { useTranslation } from 'react-i18next';
import { Button, ErrorMessage, Modal, Stepper } from '@asset-metrix/portal-ui';

import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

import { InvestmentComplianceLimitDataLevel } from './createInvestmentLimitModel';
import { DenominatorStep } from './DenominatorStep';
import { OverviewStep } from './OverviewStep';
import { ParameterStep } from './ParameterStep';
import { ReviewStep } from './ReviewStep';
import { useInvestmentLimitWizard } from './useInvestmentLimitWizard';
import { WorkflowStep } from './WorkflowStep';

import styles from './createInvestmentLimitModal.module.scss';

export type CreateInvestmentLimitModalProps = {
  contentId: string;
  opened: boolean;
  onApply: (data: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
  dataLevels: InvestmentComplianceLimitDataLevel[];
  rowColumnKeys: readonly string[];
};

export function CreateInvestmentLimitModal({
  contentId,
  opened,
  onApply,
  onClose,
  dataLevels,
  rowColumnKeys,
}: CreateInvestmentLimitModalProps) {
  const { t } = useTranslation();
  const {
    activeStep,
    activeStepKey,
    canContinue,
    draft,
    error,
    goBack,
    handlePrimaryAction,
    includeDenominator,
    isSaving,
    resetAndClose,
    updateDenominator,
    updateDraft,
    updateParameter,
    wizardSteps,
  } = useInvestmentLimitWizard({
    dataLevels,
    onApply,
    onClose,
    rowColumnKeys,
  });

  return (
    <Modal
      actions={
        <>
          <Button variant="secondary" onClick={resetAndClose}>
            {t('app.investmentLimit.cancel')}
          </Button>
          {activeStep > 0 && (
            <Button disabled={isSaving} variant="secondary" onClick={goBack}>
              {t('app.investmentLimit.back')}
            </Button>
          )}
          <Button
            data-testid="create-investment-limit-primary"
            disabled={!canContinue}
            loading={isSaving}
            onClick={handlePrimaryAction}
          >
            {activeStep === wizardSteps.length - 1
              ? t('app.investmentLimit.create')
              : t('app.investmentLimit.next')}
          </Button>
        </>
      }
      contentClassName={styles['limit-modal-content']}
      contentHeight="90vh"
      footerAction={
        error != null ? (
          <ErrorMessage className={styles['message']}>{error}</ErrorMessage>
        ) : undefined
      }
      modalType="action"
      opened={opened}
      onClose={resetAndClose}
      size={pixelSizeHelperPortalUi(1200)}
      title={t('app.investmentLimit.title')}
    >
      <Stepper
        active={activeStep}
        allowStepClick={false}
        steps={wizardSteps.map((step) => ({
          label: t(`app.investmentLimit.steps.${step.key}`),
        }))}
      />
      {activeStepKey === 'overview' && (
        <OverviewStep draft={draft} onDraftChange={updateDraft} />
      )}
      {activeStepKey === 'parameter' && (
        <ParameterStep
          contentId={contentId}
          dataLevels={dataLevels}
          includeDenominator={includeDenominator}
          limitMode={draft.limitMode}
          onParameterChange={updateParameter}
          parameter={draft.parameter}
        />
      )}
      {activeStepKey === 'denominator' &&
        draft.parameter.denominator != null && (
          <DenominatorStep
            contentId={contentId}
            dataLevels={dataLevels}
            denominator={draft.parameter.denominator}
            limitMode={draft.limitMode}
            onDenominatorChange={updateDenominator}
            onParameterChange={updateParameter}
            parameter={draft.parameter}
          />
        )}
      {activeStepKey === 'workflow' && (
        <WorkflowStep draft={draft} onDraftChange={updateDraft} />
      )}
      {activeStepKey === 'review' && (
        <ReviewStep
          contentId={contentId}
          dataLevels={dataLevels}
          draft={draft}
        />
      )}
    </Modal>
  );
}
